import { IngestJobSummary } from 'interfaces/ingestJob';
import type { Study } from 'interfaces/study';
import { stringify } from 'qs';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';

export interface Lab {
  id: string;
  name: string;
  isStagingLab?: boolean;
  isTestLab?: boolean;
  customerBucketName?: string | null;
  defaultStudySettings?: Study['settings'];
  deletedAt?: string | null;
  deletedBy?: string | null;
}

export interface CreateLabRequest {
  labName: string;
  isStagingLab?: boolean;
  isTestLab?: boolean;
  labelSettings?: Record<string, unknown>;
}

export interface CreateLabResponse {
  labId: string;
  bucketProvisioned: boolean;
  bucketProvisioningError?: string | null;
  isStagingLab?: boolean;
  isTestLab?: boolean;
  customerBucketName?: string | null;
}

export interface UpdateLabRequest {
  labName?: string;
  isTestLab?: boolean;
  labelSettings?: Record<string, unknown>;
  defaultStudySettings?: Study['settings'];
}

export interface DeleteLabResponse {
  labId: string;
}

export interface ProvisionLabBucketResponse {
  labId: string;
  bucketProvisioned: boolean;
  bucketProvisioningError?: string | null;
  isStagingLab?: boolean;
  isTestLab?: boolean;
  customerBucketName?: string | null;
}

export interface LabUploadLatestBatch {
  uploadedAt: string;
  studyId: string;
  studyName: string;
  fileCount: number;
}

export interface LabUploadStudySummary {
  lastUploadedAt: string;
}

export interface LabUploadSummary {
  latestBatch: LabUploadLatestBatch | null;
  byStudy: Record<string, LabUploadStudySummary>;
  statistics: {
    lostSlides: number;
    lostSlidesCapped: boolean;
    hiddenSlides: number;
  };
}

export type LabLostAndFoundMissingReason = 'missingIngest' | 'missingCaseAssignment';

export interface LabLostAndFoundCase {
  id: number;
  displayName: string;
}

export interface LabLostAndFoundUpload {
  objectKey: string;
  sourceStudyId: string;
  sourceStudyName: string;
  size: number;
  uploadedAt: string;
  missingIngest: boolean;
  missingCaseAssignment: boolean;
  /** Newest ingest attempt by effective time; may be archived or terminal. */
  latestIngestJob: IngestJobSummary | null;
  /** Newest unarchived attempt that is pending or running. */
  activeIngestJob: IngestJobSummary | null;
  /** Newest failed, failed-to-start, or cancelled attempt, including archives. */
  latestFailedIngestJob: IngestJobSummary | null;
  /** Newest completed attempt, including archived completions. */
  latestSuccessfulIngestJob: IngestJobSummary | null;
  latestSuccessfulIngestJobArchived: boolean;
  /** Newest completed attempt that is still active (not archived). */
  activeSuccessfulIngestJob: IngestJobSummary | null;
  resolvedSlideId: string | null;
  cases: LabLostAndFoundCase[];
}

export interface LabLostAndFoundResponse {
  lostUploads: LabLostAndFoundUpload[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface LabLostAndFoundParams {
  pageSize?: number;
  cursor?: string;
  sourceStudyIds?: string[];
  missingReasons?: LabLostAndFoundMissingReason[];
  uploadedAtStart?: string;
  uploadedAtEnd?: string;
  searchQuery?: string;
}

export interface LabUploadIgnoreOutcome {
  objectKey: string;
  success: boolean;
  isHidden: boolean | null;
  archivedJobIds: string[];
  error: string | null;
}

export interface LabUploadIgnoreResponse {
  outcomes: LabUploadIgnoreOutcome[];
}

interface GetLabsOptions {
  includeArchived?: boolean;
  signal?: AbortSignal;
}

export const getLabs = ({ includeArchived = false, signal }: GetLabsOptions = {}): Promise<Lab[]> => {
  return apiRequestHandlerPromise<Lab[]>({
    url: includeArchived ? 'labs?includeArchived=true' : 'labs',
    method: 'GET',
    signal,
  });
};

export const createLab = (data: CreateLabRequest): Promise<CreateLabResponse> => {
  return apiRequestHandlerPromise<CreateLabResponse>({
    url: 'labs',
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const updateLab = ({ labId, data }: { labId: string; data: UpdateLabRequest }): Promise<Lab> => {
  return apiRequestHandlerPromise<Lab>({
    url: `labs/${labId}`,
    method: 'PATCH',
    data: JSON.stringify(data),
  });
};

export const deleteLab = (labId: string): Promise<DeleteLabResponse> => {
  return apiRequestHandlerPromise<DeleteLabResponse>({
    url: `labs/${labId}`,
    method: 'DELETE',
  });
};

export const provisionLabCustomerBucket = (labId: string): Promise<ProvisionLabBucketResponse> => {
  return apiRequestHandlerPromise<ProvisionLabBucketResponse>({
    url: `labs/${labId}/provision-customer-bucket`,
    method: 'POST',
  });
};

export const getLabUploadSummary = (labId: string, signal?: AbortSignal): Promise<LabUploadSummary> => {
  return apiRequestHandlerPromise<LabUploadSummary>({
    url: `labs/${labId}/uploads/summary`,
    method: 'GET',
    signal,
  });
};

export const getLabLostAndFound = (
  labId: string,
  params: LabLostAndFoundParams = {},
  signal?: AbortSignal
): Promise<LabLostAndFoundResponse> => {
  const query = stringify(params, { arrayFormat: 'repeat', skipNulls: true });

  return apiRequestHandlerPromise<LabLostAndFoundResponse>({
    url: `labs/${labId}/uploads/lost-and-found${query ? `?${query}` : ''}`,
    method: 'GET',
    signal,
  });
};

export const ignoreLabUploads = (labId: string, objectKeys: string[]): Promise<LabUploadIgnoreResponse> => {
  return apiRequestHandlerPromise<LabUploadIgnoreResponse>({
    url: `labs/${labId}/uploads/ignore`,
    method: 'POST',
    data: JSON.stringify({ objectKeys }),
  });
};

export const restoreLab = (labId: string): Promise<Lab> => {
  return apiRequestHandlerPromise<Lab>({
    url: `labs/${labId}/restore`,
    method: 'POST',
  });
};
